import { useRef, useState, type ChangeEvent } from "react";
import { uploadPhotoFile } from "../lib/photoUpload";
import { useCurrentGardenId } from "../lib/useCurrentGarden";
import "./CameraCard.css";

interface CameraCardProps {
  onUploaded: () => void;
}

export function CameraCard({ onUploaded }: CameraCardProps) {
  const gardenId = useCurrentGardenId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "uploading" }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  const onPick = () => {
    if (state.kind === "uploading") return;
    inputRef.current?.click();
  };

  const onChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!gardenId) {
      setState({ kind: "error", message: "No active garden." });
      return;
    }
    setState({ kind: "uploading" });
    try {
      await uploadPhotoFile(file, gardenId);
      setState({ kind: "idle" });
      onUploaded();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed";
      setState({ kind: "error", message });
    }
  };

  const disabled = state.kind === "uploading" || !gardenId;

  return (
    <>
      <button
        type="button"
        className="CameraCard"
        onClick={onPick}
        disabled={disabled}
        aria-label="Capture a photo"
      >
        <span className="CameraCard__btn" aria-hidden="true">
          <svg viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.8">
            <circle cx="14" cy="15" r="6" />
            <path d="M4 9h3l2-3h10l2 3h3v14H4V9z" />
          </svg>
        </span>
        <span className="CameraCard__text">
          <span className="CameraCard__title">
            {state.kind === "uploading" ? "Uploading…" : "Capture a photo"}
          </span>
          <span className="CameraCard__sub">
            {state.kind === "error" ? state.message : "Tag later"}
          </span>
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onChange}
        style={{ display: "none" }}
      />
    </>
  );
}
